<template>
  <AppSidebar>
    <div class="space-y-10">
      <!-- Page Header -->
      <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-8 pb-4">
        <div class="space-y-2">
          <div class="flex items-center gap-3 mb-1">
            <div class="w-2 h-2 bg-primary-500 rounded-full shadow-[0_0_12px_rgba(20,184,166,0.5)] animate-pulse"></div>
            <span class="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">User Management</span>
          </div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white" style="font-family: Poppins, sans-serif">
            Users Directory
          </h1>
          <p class="text-sm text-gray-500 dark:text-gray-400 max-w-md font-medium">
            Manage user credentials, platform roles, and security permissions.
          </p>
        </div>
        <div class="flex items-center gap-4">
          <button @click="showAddUserModal = true" 
            class="group relative inline-flex items-center px-6 py-3 bg-gray-900 dark:bg-primary-600 text-white text-sm font-semibold rounded-2xl hover:scale-105 transition-all shadow-lg shadow-primary-500/20 active:scale-95 overflow-hidden">
            <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            <UserPlusIcon class="w-4 h-4 mr-2" />
            Add User
          </button>
        </div>
      </div>
      
      <!-- User Statistics -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <!-- Total Users -->
        <div class="group relative bg-white dark:bg-gray-800/40 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 hover:border-blue-500/30 transition-all hover:shadow-2xl hover:shadow-gray-200/50 dark:hover:shadow-black/40 overflow-hidden">
          <div class="absolute -right-4 -top-4 w-24 h-24 bg-blue-500/5 rounded-full blur-2xl group-hover:bg-blue-500/10 transition-colors"></div>
          <div class="relative flex items-center justify-between mb-6">
            <div class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl group-hover:scale-110 transition-transform duration-500">
              <UsersIcon class="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div class="flex flex-col items-end">
               <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Total Registry</span>
              <div class="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">{{ userStore.userStats.total || 0 }}</div>
            </div>
          </div>
          <div class="h-1.5 w-full bg-gray-50 dark:bg-gray-900/50 rounded-full overflow-hidden">
            <div class="h-full bg-blue-500 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.5)]" style="width: 100%"></div>
          </div>
        </div>
        
        <!-- Active Users -->
        <div class="group relative bg-white dark:bg-gray-800/40 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 hover:border-emerald-500/30 transition-all hover:shadow-2xl hover:shadow-gray-200/50 dark:hover:shadow-black/40 overflow-hidden">
          <div class="absolute -right-4 -top-4 w-24 h-24 bg-emerald-500/5 rounded-full blur-2xl group-hover:bg-emerald-500/10 transition-colors"></div>
          <div class="relative flex items-center justify-between mb-6">
            <div class="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl group-hover:scale-110 transition-transform duration-500">
              <ShieldCheckIcon class="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div class="flex flex-col items-end">
               <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Active Accounts</span>
              <div class="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">{{ userStore.userStats.active || 0 }}</div>
            </div>
          </div>
          <div class="h-1.5 w-full bg-gray-50 dark:bg-gray-900/50 rounded-full overflow-hidden">
            <div class="h-full bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.5)]" 
              :style="{ width: userStore.userStats.total > 0 ? (userStore.userStats.active / userStore.userStats.total * 100) + '%' : '0%' }"></div>
          </div>
        </div>
        
        <!-- Clients -->
        <div class="group relative bg-white dark:bg-gray-800/40 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 hover:border-purple-500/30 transition-all hover:shadow-2xl hover:shadow-gray-200/50 dark:hover:shadow-black/40 overflow-hidden">
          <div class="absolute -right-4 -top-4 w-24 h-24 bg-purple-500/5 rounded-full blur-2xl group-hover:bg-purple-500/10 transition-colors"></div>
          <div class="relative flex items-center justify-between mb-6">
            <div class="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-2xl group-hover:scale-110 transition-transform duration-500">
              <UserGroupIcon class="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div class="flex flex-col items-end">
               <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Client Base</span>
              <div class="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">{{ userStore.userStats.clients || 0 }}</div>
            </div>
          </div>
          <div class="h-1.5 w-full bg-gray-50 dark:bg-gray-900/50 rounded-full overflow-hidden">
            <div class="h-full bg-purple-500 rounded-full shadow-[0_0_8px_rgba(139,92,246,0.5)]"
              :style="{ width: userStore.userStats.total > 0 ? (userStore.userStats.clients / userStore.userStats.total * 100) + '%' : '0%' }"></div>
          </div>
        </div>
        
        <!-- Admins -->
        <div class="group relative bg-white dark:bg-gray-800/40 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 hover:border-amber-500/30 transition-all hover:shadow-2xl hover:shadow-gray-200/50 dark:hover:shadow-black/40 overflow-hidden">
          <div class="absolute -right-4 -top-4 w-24 h-24 bg-amber-500/5 rounded-full blur-2xl group-hover:bg-amber-500/10 transition-colors"></div>
          <div class="relative flex items-center justify-between mb-6">
            <div class="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl group-hover:scale-110 transition-transform duration-500">
              <SparklesIcon class="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div class="flex flex-col items-end">
               <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Staff / Admins</span>
              <div class="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">{{ userStore.userStats.admins || 0 }}</div>
            </div>
          </div>
          <div class="h-1.5 w-full bg-gray-50 dark:bg-gray-900/50 rounded-full overflow-hidden">
            <div class="h-full bg-amber-500 rounded-full shadow-[0_0_8px_rgba(245,158,11,0.5)]"
              :style="{ width: userStore.userStats.total > 0 ? (userStore.userStats.admins / userStore.userStats.total * 100) + '%' : '0%' }"></div>
          </div>
        </div>
      </div>
      
      <!-- Search and Filter -->
      <div class="flex flex-col lg:flex-row items-stretch lg:items-center gap-6">
        <div class="flex-1 relative group">
          <div class="absolute inset-y-0 left-6 flex items-center pointer-events-none text-gray-400 group-focus-within:text-primary-500 transition-colors">
            <MagnifyingGlassIcon class="h-5 w-5" />
          </div>
          <input
            v-model="searchQuery"
            type="text"
            class="w-full pl-16 pr-6 py-4 bg-white dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm hover:border-primary-500/30"
            placeholder="Search directory by name, email, or role..."
          />
        </div>
        <div class="flex flex-wrap items-center gap-4">
          <div class="relative min-w-[160px]">
            <select v-model="roleFilter" 
              class="w-full pl-5 pr-12 py-4 bg-white dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-xs font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all appearance-none cursor-pointer">
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="client">Client</option>
              <option value="developer">Developer</option>
            </select>
            <div class="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
              <ChevronDownIcon class="w-4 h-4" />
            </div>
          </div>
          <div class="relative min-w-[160px]">
            <select v-model="statusFilter" 
              class="w-full pl-5 pr-12 py-4 bg-white dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-xs font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all appearance-none cursor-pointer">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <div class="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
              <ChevronDownIcon class="w-4 h-4" />
            </div>
          </div>
        </div>
      </div>
      
      <!-- Users Table -->
      <div class="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-[40px] border border-white/20 dark:border-gray-800/50 shadow-[0_20px_50px_rgba(0,0,0,0.05)] dark:shadow-none overflow-hidden ring-1 ring-black/5 dark:ring-white/5">
        <div class="p-8 border-b border-gray-100 dark:border-gray-800/50 flex items-center justify-between bg-white/20 dark:bg-gray-900/20">
          <div class="flex items-center gap-4">
            <div class="p-3 bg-primary-500/10 rounded-2xl">
              <UserGroupIcon class="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <h2 class="text-lg font-bold text-gray-900 dark:text-white" style="font-family: Poppins, sans-serif">Registered Users</h2>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 font-medium">{{ filteredUsers.length }} total accounts</p>
            </div>
          </div>
        </div>
        <div class="overflow-x-auto custom-scrollbar">
          <table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800/50">
            <thead>
              <tr class="bg-gray-50/50 dark:bg-gray-900/40">
                <th class="px-8 py-5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">
                  Profile
                </th>
                <th class="px-8 py-5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">
                  Role
                </th>
                <th class="px-8 py-5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th class="px-8 py-5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">
                  Joined
                </th>
                <th class="px-8 py-5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-800/30">
              <tr v-for="user in filteredUsers" :key="user.id" class="hover:bg-gray-50/50 dark:hover:bg-primary-900/5 transition-all duration-300 group">
                <td class="px-8 py-6 whitespace-nowrap">
                  <div class="flex items-center gap-5">
                    <div class="relative">
                      <div class="h-12 w-12 rounded-2xl bg-gradient-to-br from-primary-100 to-blue-50 dark:from-primary-900 dark:to-blue-900 flex items-center justify-center border border-white dark:border-gray-700 shadow-sm transition-transform group-hover:scale-110">
                        <span class="text-primary-600 dark:text-primary-400 font-bold text-lg">
                          {{ user.name.charAt(0) }}
                        </span>
                      </div>
                      <div class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-gray-900 shadow-sm"
                        :class="user.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'"></div>
                    </div>
                    <div>
                      <div class="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 transition-colors">{{ user.name }}</div>
                      <div class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ user.email }}</div>
                      <div v-if="user.company" class="mt-1">
                        <span class="text-[10px] font-medium text-primary-600 dark:text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded-full border border-primary-500/20">
                          {{ user.company }}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-8 py-6 whitespace-nowrap">
                  <span 
                    class="px-3 py-1 inline-flex text-xs font-medium rounded-lg capitalize border shadow-sm transition-all"
                    :class="{
                      'bg-purple-50 text-purple-700 border-purple-100 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800/50': user.role === 'admin',
                      'bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/50': user.role === 'client',
                      'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/50': user.role === 'developer'
                    }"
                  >
                    {{ user.role }}
                  </span>
                </td>
                <td class="px-8 py-6 whitespace-nowrap">
                  <span 
                    class="px-3 py-1 inline-flex text-xs font-medium rounded-lg capitalize border shadow-sm transition-all"
                    :class="user.status === 'active' 
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/50' 
                      : 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-800/50'"
                  >
                    {{ user.status || 'active' }}
                  </span>
                </td>
                <td class="px-8 py-6 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {{ formatDate(user.created_at) }}
                </td>
                <td class="px-8 py-6 whitespace-nowrap text-right">
                  <div class="flex justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      @click="editUser(user)"
                      class="p-2.5 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-all"
                      title="Edit Account">
                    >
                      <PencilSquareIcon class="w-5 h-5" />
                    </button>
                    <button 
                      @click="toggleUserStatus(user)"
                      class="p-2.5 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-xl transition-all"
                      :title="user.status === 'active' ? 'Suspend Account' : 'Activate Account'"
                    >
                      <ArrowPathIcon class="w-5 h-5" />
                    </button>
                    <button 
                      @click="deleteUser(user)"
                      class="p-2.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all disabled:opacity-30"
                      :disabled="user.role === 'admin'"
                      title="Delete Account"
                    >
                      <TrashIcon class="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    
    <!-- Add/Edit User Modal -->
    <div v-if="showAddUserModal || editingUser" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
        <div class="fixed inset-0 bg-gray-900/40 dark:bg-black/60 backdrop-blur-md transition-opacity" @click="closeModal"></div>

        <div class="relative inline-block align-bottom bg-white dark:bg-gray-900 rounded-[40px] text-left overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.2)] transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full border border-white/20 dark:border-gray-800/50">
          <!-- Modal Header -->
          <div class="px-10 py-8 border-b border-gray-100 dark:border-gray-800/50 flex items-center justify-between bg-white/20 dark:bg-gray-900/20 mb-px">
            <div class="flex items-center gap-4">
              <div class="p-3 bg-primary-500/10 rounded-2xl">
                <UserCircleIcon class="w-6 h-6 text-primary-500" />
              </div>
              <h2 class="text-xl font-bold text-gray-900 dark:text-white" style="font-family: Poppins, sans-serif">
                {{ editingUser ? 'Edit User' : 'Add New User' }}
              </h2>
            </div>
            <button @click="closeModal" class="p-3 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-2xl transition-all">
              <XMarkIcon class="w-6 h-6" />
            </button>
          </div>
        
          <form @submit.prevent="saveUser">
            <!-- Modal Body -->
            <div class="max-h-[70vh] overflow-y-auto custom-scrollbar">
              <!-- Tab Header -->
              <div class="flex px-10 bg-gray-50/50 dark:bg-gray-800/40 border-b border-gray-100 dark:border-gray-800/50 sticky top-0 z-10">
                <button type="button" @click="activeTab = 'basic'"
                  class="px-8 py-5 text-[10px] font-bold uppercase tracking-[0.2em] transition-all relative group"
                  :class="activeTab === 'basic' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'">
                  Account Details
                  <div v-if="activeTab === 'basic'" class="absolute bottom-0 left-0 right-0 h-1 bg-primary-500 rounded-t-full shadow-[0_-4px_12px_rgba(20,184,166,0.4)]"></div>
                </button>
                <button type="button" @click="activeTab = 'permissions'"
                  class="px-8 py-5 text-[10px] font-bold uppercase tracking-[0.2em] transition-all relative group"
                  :class="activeTab === 'permissions' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'">
                  Permissions
                  <div v-if="activeTab === 'permissions'" class="absolute bottom-0 left-0 right-0 h-1 bg-primary-500 rounded-t-full shadow-[0_-4px_12px_rgba(20,184,166,0.4)]"></div>
                </button>
              </div>

              <div class="p-10 space-y-10">
                <!-- Basic Info Tab -->
                <div v-show="activeTab === 'basic'" class="space-y-8">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div class="space-y-2">
                      <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">Full Name *</label>
                      <input v-model="userForm.name" type="text" required
                        class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium"
                        placeholder="John Doe" />
                    </div>
                    
                    <div class="space-y-2">
                      <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">Email Address *</label>
                      <input v-model="userForm.email" type="email" required
                        class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium"
                        placeholder="john@example.com" />
                    </div>
                  </div>
                  
                  <div class="space-y-2">
                    <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">Company Name</label>
                    <input v-model="userForm.company_name" type="text"
                      class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium"
                      placeholder="Enter company name" />
                  </div>
                  
                  <div class="space-y-4">
                    <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">User Role *</label>
                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div v-for="role in ['admin', 'developer', 'client']" :key="role" @click="userForm.role = role"
                        class="relative flex flex-col cursor-pointer rounded-2xl border p-5 transition-all group overflow-hidden"
                        :class="userForm.role === role 
                          ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/10 shadow-lg shadow-primary-500/5' 
                          : 'border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900/50 hover:border-primary-200 dark:hover:border-primary-800'">
                        <div v-if="userForm.role === role" class="absolute -right-2 -top-2 w-12 h-12 bg-primary-500/10 rounded-full blur-xl"></div>
                        <div class="flex items-center justify-between mb-3 relative">
                          <span class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-tight capitalize">{{ role }}</span>
                          <div v-if="userForm.role === role" class="w-4 h-4 rounded-full bg-primary-500 flex items-center justify-center shadow-[0_0_8px_rgba(20,184,166,0.6)]">
                            <CheckCircleIcon class="w-3.5 h-3.5 text-white" />
                          </div>
                        </div>
                        <span class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest relative">
                          {{ role === 'admin' ? 'Full Access' : (role === 'developer' ? 'Developer Access' : 'Client Access') }}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div v-if="!editingUser" class="space-y-2">
                      <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">Password *</label>
                      <input v-model="userForm.password" type="password" required
                        class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium"
                        placeholder="••••••••" />
                    </div>

                    <div class="space-y-2">
                      <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">Account Status *</label>
                      <div class="relative">
                        <select v-model="userForm.status" 
                          class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-800/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-sm font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all appearance-none cursor-pointer" required>
                          <option value="active">Active</option>
                          <option value="suspended">Suspended</option>
                          <option value="inactive">Inactive</option>
                        </select>
                        <div class="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                          <ChevronDownIcon class="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Permissions Tab -->
                <div v-show="activeTab === 'permissions'" class="space-y-10">
                  <div v-for="(category, catName) in permissionCategories" :key="catName" class="space-y-6">
                    <div class="flex items-center gap-3">
                      <div class="w-1.5 h-1.5 bg-primary-500 rounded-full shadow-[0_0_8px_rgba(20,184,166,0.5)]"></div>
                      <h3 class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest">{{ catName }} Permissions</h3>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div v-for="permission in category" :key="permission.id" 
                        class="group flex items-center justify-between p-5 bg-gray-50/50 dark:bg-gray-800/20 rounded-[28px] border border-gray-100 dark:border-gray-700/50 hover:border-primary-500/30 transition-all duration-300">
                        <div class="flex-1 pr-4">
                          <div class="text-[12px] font-bold text-gray-900 dark:text-white uppercase tracking-tight">{{ permission.name }}</div>
                          <div class="text-[9px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-1">{{ permission.description }}</div>
                        </div>
                        <label class="relative inline-flex items-center cursor-pointer flex-shrink-0">
                          <input type="checkbox" v-model="userForm.permissions[permission.id]" class="sr-only peer">
                          <div class="w-12 h-6.5 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:rounded-full after:h-5 after:w-5 after:shadow-lg after:transition-all peer-checked:bg-primary-500 shadow-inner"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Modal Footer -->
            <div class="px-10 py-8 bg-gray-50/50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-800/50 flex justify-end gap-4 relative overflow-hidden backdrop-blur-md">
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_3s_infinite]"></div>
              <button type="button" @click="closeModal" 
                class="px-6 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 rounded-2xl text-sm font-medium text-gray-500 hover:text-gray-900 dark:hover:text-white transition-all active:scale-95 shadow-sm">
                Cancel
              </button>
              <button type="submit" 
                class="group relative px-8 py-3 bg-gray-900 dark:bg-primary-600 text-white text-sm font-semibold rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95 overflow-hidden">
                <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                {{ editingUser ? 'Save Changes' : 'Create User' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </AppSidebar>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/users'
import { useUIStore } from '@/stores/ui'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import { 
    PlusIcon, MagnifyingGlassIcon, FunnelIcon, 
    UsersIcon, ShieldCheckIcon, UserGroupIcon, 
    SparklesIcon, XMarkIcon, PencilSquareIcon, 
    TrashIcon, UserPlusIcon, ChevronDownIcon,
    ArrowPathIcon, CheckCircleIcon, KeyIcon
} from '@heroicons/vue/24/outline'

const userStore = useUserStore()
const uiStore = useUIStore()

const activeTab = ref('basic')

const permissionCategories = {
  'Management': [
    { id: 'manage_users', name: 'User Management', description: 'Create, edit, and delete user accounts' },
    { id: 'access_activity_logs', name: 'Activity Logs', description: 'View system logs and user activities' },
    { id: 'manage_settings', name: 'System Settings', description: 'Modify global site configurations' }
  ],
  'Content & Services': [
    { id: 'manage_services', name: 'Service Management', description: 'Manage services and categories' },
    { id: 'manage_products', name: 'Product Catalog', description: 'Manage products and downloads' },
    { id: 'manage_dev_profiles', name: 'Dev Profiles', description: 'Manage developer profiles and assignments' },
    { id: 'manage_content', name: 'Legal & Static Content', description: 'Edit legal pages and other content' }
  ],
  'Operations': [
    { id: 'manage_orders', name: 'Order Processing', description: 'Manage customer orders and status' },
    { id: 'manage_invoices', name: 'Invoice Management', description: 'Generate and manage billing' },
    { id: 'manage_tickets', name: 'Support Tickets', description: 'Respond to customer support requests' }
  ]
}

// State
const showAddUserModal = ref(false)
const editingUser = ref(null)
const searchQuery = ref('')
const roleFilter = ref('')
const statusFilter = ref('')

// User form
const userForm = reactive({
  name: '',
  email: '',
  company_name: '',
  role: 'client',
  status: 'active',
  password: '',
  permissions: {}
})

// Initialize
onMounted(() => {
  userStore.initializeUsers()
})

// Computed
const filteredUsers = computed(() => {
  let users = userStore.users
  
  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    users = users.filter(user => 
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      (user.company && user.company.toLowerCase().includes(query))
    )
  }
  
  // Role filter
  if (roleFilter.value) {
    users = users.filter(user => user.role === roleFilter.value)
  }
  
  // Status filter
  if (statusFilter.value) {
    users = users.filter(user => (user.status || 'active') === statusFilter.value)
  }
  
  return users
})

// Methods
const editUser = (user) => {
  editingUser.value = user
  activeTab.value = 'basic'
  
  // Initialize permissions object with all-false defaults, then set known permissions
  const initialPermissions = {}
  Object.values(permissionCategories).flat().forEach(p => {
    initialPermissions[p.id] = false
  })
  
  if (user.permissions) {
    if (Array.isArray(user.permissions)) {
      user.permissions.forEach(pId => {
        initialPermissions[pId] = true
      })
    } else if (typeof user.permissions === 'object') {
      Object.keys(user.permissions).forEach(pId => {
        initialPermissions[pId] = !!user.permissions[pId]
      })
    }
  }

  Object.assign(userForm, {
    name: user.name,
    email: user.email,
    company_name: user.company_name || '',
    role: user.role,
    status: user.status || 'active',
    password: '',
    permissions: initialPermissions
  })
}

const closeModal = () => {
  showAddUserModal.value = false
  editingUser.value = null
  resetForm()
}

const resetForm = () => {
  activeTab.value = 'basic'
  Object.assign(userForm, {
    name: '',
    email: '',
    company_name: '',
    role: 'client',
    status: 'active',
    password: '',
    permissions: {}
  })
}

const saveUser = async () => {
  try {
    // Normalize permissions to an array of IDs if it's an admin or developer
    const finalPermissions = (userForm.role === 'admin' || userForm.role === 'developer')
      ? Object.keys(userForm.permissions).filter(key => userForm.permissions[key])
      : []

    const userData = {
      ...userForm,
      permissions: finalPermissions
    }

    if (editingUser.value) {
      delete userData.password // Don't update password in edit mode
      await userStore.updateUser(editingUser.value.id, userData)
    } else {
      await userStore.addUser(userData)
    }
    closeModal()
  } catch (error) {
    console.error('Failed to save user:', error)
  }
}

const toggleUserStatus = async (user) => {
  await userStore.toggleUserStatus(user.id)
}

const deleteUser = async (user) => {
  if (user.role === 'admin') {
    uiStore.showNotification({
      type: 'error',
      title: 'Cannot Delete',
      message: 'Admin users cannot be deleted.'
    })
    return
  }
  
  if (confirm(`Are you sure you want to delete "${user.name}"?`)) {
    await userStore.deleteUser(user.id)
  }
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}
</script>
